/**
 * Word Lists List View
 *
 * Admin view for managing word lists with filtering, pagination, and CRUD operations.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInitialData } from '../../../contexts/InitialDataContext';
import { getSkillLevelEnglishName } from '../../../services/initialDataApi';
import {
  useWordLists,
  useDeleteWordList,
  useSetPublished,
  exportWordList,
} from '../services/wordListsApi';
import { WordsNavigation } from '../components/WordsNavigation';
import { ImportWordListDialog } from '../components/ImportWordListDialog';
import type { WordList } from '../types/wordList.types';

export function WordListsListView() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState<boolean | undefined>(undefined);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const pageSize = 20;

  // Get language filter from URL params (set by WordsNavigation)
  const languageFilter = searchParams.get('lang') || '';

  const { state: initialDataState } = useInitialData();
  const languages = initialDataState.data?.languages ?? [];
  const skillLevels = initialDataState.data?.skillLevels ?? [];

  const { data, isLoading, error } = useWordLists({
    type: typeFilter || undefined,
    languageCode: languageFilter || undefined,
    isPublished: publishedFilter,
    page,
    pageSize,
  });

  const deleteWordList = useDeleteWordList();
  const setPublished = useSetPublished();

  const handleDelete = async (wordList: WordList) => {
    if (
      !confirm(`Are you sure you want to delete "${wordList.name}"? This action cannot be undone.`)
    ) {
      return;
    }

    deleteWordList.mutate(wordList.id, {
      onSuccess: () => {
        toast.success(`Word list "${wordList.name}" deleted successfully`);
      },
      onError: (error) => {
        toast.error(`Failed to delete word list: ${(error as Error).message}`);
      },
    });
  };

  const handleTogglePublish = async (wordList: WordList) => {
    setPublished.mutate(
      { id: wordList.id, isPublished: !wordList.isPublished },
      {
        onSuccess: () => {
          toast.success(
            `Word list "${wordList.name}" ${wordList.isPublished ? 'unpublished' : 'published'} successfully`
          );
        },
        onError: (error) => {
          toast.error(`Failed to update publish state: ${(error as Error).message}`);
        },
      }
    );
  };

  const handleExport = async (wordList: WordList) => {
    setExportingId(wordList.id);
    try {
      await exportWordList(wordList.id);
      toast.success(`Word list "${wordList.name}" exported successfully`);
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setExportingId(null);
    }
  };

  const getLanguageName = (code: string) => {
    const lang = languages.find((l) => l.code === code);
    return lang?.name ?? code;
  };

  const getSkillLevelName = (id: number) => {
    const level = skillLevels.find((l) => l.id === id);
    if (!level) return `Level ${id}`;
    return getSkillLevelEnglishName(level);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading word lists</p>
          <p className="text-gray-600">{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <WordsNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Word Lists</h1>
          <p className="text-gray-600 mt-1">
            Manage curated vocabulary lists by type, language, and skill level.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportDialog(true)}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
          >
            Import Word List
          </button>
          <Link
            to="/words/lists/new"
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Create Word List
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Types</option>
            <option value="exams">Exams</option>
            <option value="topics">Topics</option>
            <option value="frequency">Frequency</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={publishedFilter === undefined ? '' : publishedFilter ? 'true' : 'false'}
            onChange={(e) => {
              setPublishedFilter(e.target.value === '' ? undefined : e.target.value === 'true');
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Statuses</option>
            <option value="true">Published</option>
            <option value="false">Unpublished</option>
          </select>
        </div>
      </div>

      {/* Word Lists Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Language
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Skill Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Words
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.items.map((wordList) => (
              <tr key={wordList.id} data-testid={`word-list-row-${wordList.id}`}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{wordList.name}</td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                    {wordList.type}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {getLanguageName(wordList.languageCode)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {getSkillLevelName(wordList.skillLevelId)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{wordList.wordCount}</td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleTogglePublish(wordList)}
                    disabled={setPublished.isPending}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                      wordList.isPublished
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {wordList.isPublished ? 'Published' : 'Draft'}
                  </button>
                </td>
                <td className="px-6 py-4 text-sm font-medium space-x-2">
                  <Link
                    to={`/words/lists/${wordList.id}/words`}
                    className="text-green-600 hover:text-green-900"
                  >
                    Words
                  </Link>
                  <Link
                    to={`/words/lists/${wordList.id}/edit`}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => handleExport(wordList)}
                    disabled={exportingId === wordList.id}
                    className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                  >
                    {exportingId === wordList.id ? 'Exporting...' : 'Export'}
                  </button>
                  <button
                    onClick={() => handleDelete(wordList)}
                    disabled={deleteWordList.isPending}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {data?.items.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center mt-4">
          <p className="text-gray-600 text-lg">No word lists found matching your filters.</p>
          <Link
            to="/words/lists/new"
            className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Create Your First Word List
          </Link>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data?.total ?? 0)} of{' '}
            {data?.total ?? 0} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-3 py-1">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Import Word List Dialog */}
      {showImportDialog && <ImportWordListDialog onClose={() => setShowImportDialog(false)} />}
    </div>
  );
}
